import { useEffect, useMemo, useState } from "react";
import {
  type Account,
  type AccountBalance,
  ApiError,
  api,
  type BankImport,
  type BankLine,
  type BankLineDetail,
  type BankLinePage,
  type DuplicateGroup,
  type MatchKind,
  type MatchSuggestion,
  formatMoney,
  type ImportPreview,
  type Voucher,
} from "./api";
import { singaporeDay, singaporeToday } from "./dates";
import { hashParam } from "./layout/router";
import { SelectField } from "./fields";
import { PageHeader } from "./layout/Layout";
import {
  Actions,
  Alert,
  Badge,
  Box,
  Empty,
  Field,
  IconAction,
  Loading,
  Modal,
  Offcanvas,
  Pager,
  PanelSection,
  StatCard,
  Table,
  Tabs,
  Th,
  TwoLine,
  Working,
} from "./ui";
import { Attachments } from "./attachments";
import { BankPeek } from "./BankPeek";
import { FlagButton, FlagNote } from "./FlagButton";
import { AlertLog } from "./AlertLog";
import { EarlierSale } from "./EarlierSale";
import { BankDocumentItems, DocumentAdjustments } from "./BankDocumentItems";

/** A line as a person says it: "3,888.00 to POKETEARTCG", "88.00 from LING WEI". */
function describe(line: { readonly amountCents: number; readonly counterparty: string | null }): string {
  const money = formatMoney(Math.abs(line.amountCents));
  if (!line.counterparty) return `The transaction of ${money}`;
  return line.amountCents > 0 ? `${money} from ${line.counterparty}` : `${money} to ${line.counterparty}`;
}

/** Rows per page of bank lines. The server pages them; this must match its page size. */
const LINES_PER_PAGE = 25;

/** A statement bigger than this is not an OCBC export of one account. */
const MAX_FILE_BYTES = 5 * 1024 * 1024;

/**
 * The bank (quotation A5 and A6, with the balance check A8 across the top).
 *
 * Statements come in from the OCBC export, previewed first so nothing reaches the books
 * until someone has seen what it will add. The lines are the bank's own, newest first.
 * Each is matched to what it was for: the system suggests, a person confirms, straight
 * from the table or in the panel, where a match can also be chosen by hand or undone.
 */
export function Bank(): React.ReactElement {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [balances, setBalances] = useState<AccountBalance[]>([]);
  const [imports, setImports] = useState<BankImport[]>([]);
  const [accountId, setAccountId] = useState("");
  const [lines, setLines] = useState<BankLinePage | undefined>();
  const [search, setSearch] = useState("");
  const [direction, setDirection] = useState<"" | "in" | "out">("");
  const [unmatched, setUnmatched] = useState(false);
  /** Lines set aside are left out unless asked for (D, 15 October 2026). */
  const [aside, setAside] = useState(false);
  /** A day or days to go straight to (D, 18 October 2026: "it has like 1, 2, 3... 42"). */
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [openLine, setOpenLine] = useState<BankLine | undefined>();
  /** Lines in twice from earlier imports, to set aside (D, 16 October 2026). */
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);
  const [reviewingDuplicates, setReviewingDuplicates] = useState(false);

  const bankAccounts = useMemo(() => accounts.filter((account) => account.kind === "bank" && account.isActive), [accounts]);

  async function loadAccounts(): Promise<void> {
    try {
      const [accountList, summary, history] = await Promise.all([api.accounts(), api.balances(), api.bankImports()]);
      setAccounts(accountList.accounts);
      setBalances(summary.accounts);
      setImports(history.imports);
      const firstBank =
        accountList.accounts.find((account) => account.kind === "bank" && account.ownerKind === "company") ??
        accountList.accounts.find((account) => account.kind === "bank");
      // The account chosen in the menu (D, 15 October 2026: "OCBC Bank Reconciliation").
      const asked = accountList.accounts.find((account) => account.id === hashParam("account") && account.kind === "bank");
      setAccountId((current) => current || asked?.id || firstBank?.id || "");
      setError(undefined);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load bank reconciliation");
    } finally {
      setLoading(false);
    }
  }

  async function loadLines(): Promise<void> {
    if (!accountId) return;
    try {
      setLines(await api.bankLines({ accountId, search, direction, unmatched, aside, from, to, page }));
      setDuplicates((await api.bankDuplicates(accountId)).groups);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load bank transactions");
    }
  }

  useEffect(() => {
    void loadAccounts();
  }, []);

  /** After any match: the lines, and the figures across the top, both change. */
  function afterMatch(message: string): void {
    setNotice(message);
    setOpenLine(undefined);
    void loadLines();
    void loadAccounts();
  }

  async function confirm(line: BankLine, suggestion: MatchSuggestion): Promise<void> {
    try {
      await api.matchBankLine(line.id, { kind: suggestion.kind, targetId: suggestion.id });
      afterMatch(`${describe(line)} is matched to ${suggestion.label}.`);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not match the transaction");
    }
  }

  // Searching as someone types, but not on every key.
  useEffect(() => {
    const timer = window.setTimeout(() => void loadLines(), search ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [accountId, search, direction, unmatched, aside, from, to, page]);

  if (loading) return <Loading />;

  const balance = balances.find((entry) => entry.accountId === accountId);
  const gap = balance?.differenceCents ?? null;
  const filtered = Boolean(search || direction || unmatched);

  return (
    <>
      <PageHeader
        title={`${accounts.find((account) => account.id === accountId)?.name ?? "Bank"} reconciliation`}
        subtitle="Match this account's statement lines to their sales, vouchers, purchases and categories. Each statement is previewed before anything is imported."
        actions={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="ti-btn ti-btn-light !mb-0"
              disabled={bankAccounts.length === 0}
              onClick={() => setExporting(true)}
            >
              <i className="ri-file-excel-2-line" /> Export bank reconciliation
            </button>
            <button
              type="button"
              className="ti-btn ti-btn-primary-full !mb-0"
              disabled={bankAccounts.length === 0}
              onClick={() => setImporting(true)}
            >
              <i className="ri-upload-2-line" /> Import bank statement
            </button>
          </div>
        }
      />

      {error ? (
        <Alert tone="danger" onClose={() => setError(undefined)}>
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert tone="success" onClose={() => setNotice(undefined)}>
          {notice}
        </Alert>
      ) : null}

      {duplicates.length > 0 ? (
        <Alert tone="warning">
          {duplicates.reduce((sum, group) => sum + group.lines.length - 1, 0)} {duplicates.length === 1 && duplicates[0]!.lines.length === 2 ? "line was" : "lines were"} imported twice: the
          same value date, amount and balance as another line, from statements that dated them differently. They make the recorded
          balance too high.
          <button type="button" className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0 ms-2" onClick={() => setReviewingDuplicates(true)}>
            Review
          </button>
        </Alert>
      ) : null}
      {reviewingDuplicates ? (
        <Duplicates
          groups={duplicates}
          onClose={() => setReviewingDuplicates(false)}
          onSetAside={async () => {
            const { setAside } = await api.setAsideDuplicates(accountId);
            setReviewingDuplicates(false);
            afterMatch(`${setAside} ${setAside === 1 ? "copy" : "copies"} set aside. The recorded balance no longer counts them.`);
          }}
          onOne={() => {
            setNotice("Set aside. The recorded balance no longer counts it.");
            void loadLines();
            void loadAccounts();
          }}
        />
      ) : null}
      {bankAccounts.length === 0 ? (
        <Box title="Bank reconciliation">
          <Empty icon="ri-bank-line">Add the OCBC account under Financial accounts first. Its statements can then be imported here.</Empty>
        </Box>
      ) : (
        <>
          <div className="grid grid-cols-12 gap-x-6">
            <div className="xl:col-span-3 md:col-span-6 col-span-12">
              <StatCard
                label="Statement balance"
                value={balance?.bankBalanceCents == null ? "Not available" : formatMoney(balance.bankBalanceCents)}
                icon="ti ti-building-bank"
                tone="primary"
                note={balance?.lastLineDate ? `Last transaction ${balance.lastLineDate}` : "No statement imported yet"}
              />
            </div>
            <div className="xl:col-span-3 md:col-span-6 col-span-12">
              <StatCard
                label="Recorded account balance"
                value={balance ? formatMoney(balance.ourBalanceCents) : "0.00"}
                icon="ti ti-book"
                tone="secondary"
                note="Opening balance plus all transactions"
              />
            </div>
            <div className="xl:col-span-3 md:col-span-6 col-span-12">
              <StatCard
                label="Balance difference"
                value={gap === null ? "Not available" : formatMoney(Math.abs(gap))}
                icon="ti ti-scale"
                tone={gap === null ? "info" : gap === 0 ? "success" : "danger"}
                note={
                  gap === null
                    ? balance?.lastLineDate
                      ? "This statement export gives no running balance"
                      : "Available once a statement is imported"
                    : gap === 0
                      ? "Matches the statement balance"
                      : `Recorded balance is ${gap > 0 ? "higher" : "lower"}`
                }
                {...(gap !== null && gap !== 0 ? { noteTone: "danger" as const } : {})}
              />
            </div>
            <div className="xl:col-span-3 md:col-span-6 col-span-12">
              <StatCard
                label="Unmatched transactions"
                value={balance?.unmatchedCount ?? 0}
                icon="ti ti-arrows-exchange"
                tone={balance?.unmatchedCount ? "warning" : "success"}
                note={balance?.unmatchedCount ? "Match them below" : "All transactions matched"}
              />
            </div>
          </div>

          {/* What the alerts reported, with what each was for, before the statement comes (Daren, 19 October 2026). */}
          <AlertLog />

          <Box title="Bank transactions" count={lines?.total ?? 0}>
            <div className="grid grid-cols-12 gap-4 mb-4">
              <div className={`${bankAccounts.length > 1 ? "xl:col-span-4" : "xl:col-span-6"} col-span-12`}>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="ri-search-line" />
                  </span>
                  <input
                    id="bank-search"
                    type="text"
                    className="form-control"
                    value={search}
                    placeholder="Search by amount, payer or payee, category, note, sale or voucher number"
                    aria-label="Search the lines"
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>
              {bankAccounts.length > 1 ? (
                <div className="xl:col-span-2 sm:col-span-4 col-span-12">
                  <SelectField
                    id="bank-account"
                    label="Account"
                    value={accountId}
                    options={bankAccounts.map((account) => ({ value: account.id, label: account.name }))}
                    onChange={(choice) => {
                      setAccountId(choice);
                      setPage(1);
                    }}
                  />
                </div>
              ) : null}
              <div className="xl:col-span-3 sm:col-span-4 col-span-12">
                <SelectField<"all" | "in" | "out">
                  id="bank-direction"
                  label="Money in or out"
                  value={direction || "all"}
                  options={[
                    { value: "all", label: "All fund movements" },
                    { value: "in", label: "Funds incoming" },
                    { value: "out", label: "Funds outgoing" },
                  ]}
                  onChange={(choice) => {
                    setDirection(choice === "all" ? "" : choice);
                    setPage(1);
                  }}
                />
              </div>
              <div className="xl:col-span-2 sm:col-span-3 col-span-6">
                <label htmlFor="bank-from" className="form-label">From</label>
                <input id="bank-from" type="date" className="form-control" value={from} onChange={(event) => { setFrom(event.target.value); setPage(1); }} />
              </div>
              <div className="xl:col-span-2 sm:col-span-3 col-span-6">
                <label htmlFor="bank-to" className="form-label">To</label>
                <input id="bank-to" type="date" className="form-control" value={to} onChange={(event) => { setTo(event.target.value); setPage(1); }} />
              </div>
              <div className="xl:col-span-3 sm:col-span-4 col-span-12">
                <SelectField<"all" | "open" | "aside">
                  id="bank-state"
                  label="Matched or not"
                  value={aside ? "aside" : unmatched ? "open" : "all"}
                  options={[
                    { value: "all", label: "All transactions" },
                    { value: "open", label: "Unmatched" },
                    { value: "aside", label: "Set aside" },
                  ]}
                  onChange={(choice) => {
                    setUnmatched(choice === "open");
                    setAside(choice === "aside");
                    setPage(1);
                  }}
                />
              </div>
            </div>

            {!lines || lines.total === 0 ? (
              <Empty icon="ri-file-list-3-line">
                {filtered ? "No transactions match the filters." : "No bank transactions yet. Import the statement export to add them."}
              </Empty>
            ) : (
              <Table
                head={
                  <>
                    <Th>Date</Th>
                    <Th>Payer or payee</Th>
                    <Th end>Amount</Th>
                    <Th>Payment reference</Th>
                    <Th end>Balance after transaction</Th>
                    <Th>Linked record</Th>
                    <Th end>Action</Th>
                  </>
                }
              >
                {lines.lines.map((line) => (
                  <tr key={line.id} className={`border-b border-defaultborder ${line.flagged ? "bg-danger/[0.06]" : ""}`}>
                    <td>
                      <div className="flex items-start gap-1">
                        <FlagButton kind="bank_line" id={line.id} flagged={Boolean(line.flagged)} note={line.flagNote} onChanged={() => void loadLines()} />
                        <TwoLine
                          main={line.postDate}
                          sub={line.valueDate !== line.postDate ? `Value date ${line.valueDate}` : undefined}
                        />
                      </div>
                      {line.flagged ? <FlagNote note={line.flagNote} /> : null}
                    </td>
                    <td className="!whitespace-normal min-w-[14rem]">
                      <TwoLine
                        main={line.counterparty ?? line.description}
                        sub={[line.kind, line.bankReference].filter(Boolean).join(" · ") || undefined}
                      />
                    </td>
                    <td className={`!text-end font-semibold ${line.amountCents > 0 ? "text-success" : ""}`}>
                      {line.amountCents > 0 ? `+${formatMoney(line.amountCents)}` : formatMoney(line.amountCents)}
                    </td>
                    <td className="!whitespace-normal min-w-[10rem] text-[#8c9097] dark:text-white/50">
                      {line.payerNote ?? ""}
                    </td>
                    <td className="!text-end text-[#8c9097] dark:text-white/50">
                      {line.balanceAfterCents === null ? "" : formatMoney(line.balanceAfterCents)}
                    </td>
                    <td className="!whitespace-normal min-w-[12rem]">
                      {line.isIgnored ? (
                        <Badge tone="light">Set aside</Badge>
                      ) : line.match ? (
                        <>
                          <Badge tone={line.match.kind === "category" ? "info" : "success"}>{line.match.label}</Badge>
                          {/* Its transaction category beside the PV or sale (D, 16 October 2026). */}
                          {/* Split as the purchase was keyed: each category with its part (D, 21 October 2026). */}
                          {line.match.kind !== "category" && (line.categoryParts?.length ?? 0) > 1 ? (
                            <span className="block text-[0.75rem] mt-1">
                              {line.categoryParts?.map((part) => (
                                <span key={part.label} className="block font-semibold">
                                  <i className="ri-check-line text-success" /> {part.label} {formatMoney(part.amountCents)}
                                </span>
                              ))}
                            </span>
                          ) : null}
                          {line.match.kind !== "category" && (line.categoryParts?.length ?? 0) <= 1 && line.categoryConfirmed && line.accountingCategory ? (
                            <span className="block text-[0.75rem] mt-1 font-semibold">
                              <i className="ri-check-line text-success" /> {line.accountingCategory}
                            </span>
                          ) : null}
                          {/* Guessed, not chosen: the three best to confirm with one click, as matches are (D, 16 October 2026). */}
                          {line.match.kind !== "category" && !line.categoryConfirmed ? (
                            <span className="block mt-1">
                              <span className="block text-[0.6875rem] text-[#8c9097] dark:text-white/50">Category to confirm:</span>
                              <span className="flex flex-wrap gap-1 mt-0.5">
                                {(line.categoryChoices ?? []).map((choice, index) => (
                                  <button
                                    key={choice.id}
                                    type="button"
                                    title={`File under ${choice.label}`}
                                    className={`ti-btn !py-0.5 !px-2 !text-[0.6875rem] !mb-0 ${index === 0 ? "ti-btn-primary" : "ti-btn-light"}`}
                                    onClick={() =>
                                      void api
                                        .setBankLineCategory(line.id, choice.id)
                                        .then(() => loadLines())
                                        .catch((problem: unknown) => setError(problem instanceof ApiError ? problem.message : "Could not set the category"))
                                    }
                                  >
                                    {choice.label.replace(/^.* › /, "")}
                                  </button>
                                ))}
                                <button type="button" className="ti-btn ti-btn-light !py-0.5 !px-2 !text-[0.6875rem] !mb-0" onClick={() => setOpenLine(line)}>
                                  Other
                                </button>
                              </span>
                            </span>
                          ) : null}
                        </>
                      ) : line.suggestion ? (
                        <>
                          <Badge tone="warning">Unmatched</Badge>
                          {/* The three best: one click matches (D, 18 October 2026). */}
                          <span className="block text-[0.6875rem] text-[#8c9097] dark:text-white/50 mt-1">Suggested:</span>
                          <span className="flex flex-col items-start gap-0.5">
                            {(line.suggestions?.length ? line.suggestions : [line.suggestion]).map((suggestion, index) => (
                              <button
                                key={suggestion.id}
                                type="button"
                                title={suggestion.reason}
                                className={`text-start text-[0.75rem] hover:underline ${index === 0 ? "text-primary font-semibold" : "text-defaulttextcolor"}`}
                                onClick={() => void confirm(line, suggestion)}
                              >
                                <i className="ri-check-line" /> {suggestion.label}
                              </button>
                            ))}
                          </span>
                        </>
                      ) : (
                        <Badge tone="warning">Unmatched</Badge>
                      )}
                    </td>
                    <td>
                      <Actions>
                        {line.suggestion && !line.match && !line.isIgnored ? (
                          <IconAction
                            icon="ri-check-line"
                            label={`Confirm: ${line.suggestion.label}`}
                            tone="success"
                            onClick={() => line.suggestion && void confirm(line, line.suggestion)}
                          />
                        ) : null}
                        <BankPeek line={line} onClick={() => setOpenLine(line)} />
                      </Actions>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
            {lines ? (
              <Pager total={lines.total} page={lines.page} pages={lines.pages} pageSize={LINES_PER_PAGE} onPage={setPage} />
            ) : null}
          </Box>

          <Box title="Imported statements" count={imports.length}>
            {imports.length === 0 ? (
              <Empty icon="ri-upload-cloud-2-line">No statements have been imported.</Empty>
            ) : (
              <Table
                head={
                  <>
                    <Th>Import date</Th>
                    <Th>File</Th>
                    <Th>Statement period</Th>
                    <Th end>New transactions</Th>
                    <Th end>Closing statement balance</Th>
                    <Th>Imported by</Th>
                  </>
                }
              >
                {imports.map((entry) => (
                  <tr key={entry.id} className="border-b border-defaultborder">
                    <td className="text-[#8c9097] dark:text-white/50">{singaporeDay(entry.importedAt)}</td>
                    <td>
                      <TwoLine main={entry.fileName ?? "Statement"} sub={entry.accountName} />
                    </td>
                    <td>{entry.from && entry.to ? `${entry.from} to ${entry.to}` : ""}</td>
                    <td className="!text-end font-semibold">
                      {entry.newLineCount}
                      {entry.newLineCount < entry.lineCount ? (
                        <span className="text-[#8c9097] dark:text-white/50 font-normal"> of {entry.lineCount}</span>
                      ) : null}
                    </td>
                    <td className="!text-end">
                      {entry.closingBalanceCents === null ? "" : formatMoney(entry.closingBalanceCents)}
                    </td>
                    <td>{entry.importedBy}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Box>
        </>
      )}

      {openLine ? (
        <MatchLine line={openLine} onClose={() => setOpenLine(undefined)} onDone={afterMatch} />
      ) : null}

      {importing ? (
        <ImportStatement
          accounts={bankAccounts}
          defaultAccountId={accountId}
          onClose={() => setImporting(false)}
          onImported={(message, importedInto) => {
            setImporting(false);
            setNotice(message);
            setAccountId(importedInto);
            setPage(1);
            void loadAccounts();
            void loadLines();
          }}
        />
      ) : null}
      {exporting ? <BankFile accounts={bankAccounts} onClose={() => setExporting(false)} /> : null}
    </>
  );
}

/** The two steps of an import: choose the file and see what it brings, then bring it in. */
function ImportStatement({
  accounts,
  defaultAccountId,
  onClose,
  onImported,
}: {
  readonly accounts: readonly Account[];
  readonly defaultAccountId: string;
  readonly onClose: () => void;
  readonly onImported: (message: string, accountId: string) => void;
}): React.ReactElement {
  const [accountId, setAccountId] = useState(defaultAccountId || accounts[0]?.id || "");
  const [file, setFile] = useState<{ name: string; content: string } | undefined>();
  const [preview, setPreview] = useState<ImportPreview | undefined>();
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Every change of account or file is checked again before anything can be imported.
  useEffect(() => {
    if (!file || !accountId) return;
    let live = true;
    setChecking(true);
    setPreview(undefined);
    setError(undefined);
    api
      .previewStatement({ accountId, fileName: file.name, content: file.content })
      .then((result) => {
        if (live) setPreview(result.preview);
      })
      .catch((problem: unknown) => {
        if (live) setError(problem instanceof ApiError ? problem.message : "Could not read the statement");
      })
      .finally(() => {
        if (live) setChecking(false);
      });
    return () => {
      live = false;
    };
  }, [file, accountId]);

  async function choose(chosen: File | undefined): Promise<void> {
    setError(undefined);
    setPreview(undefined);
    if (!chosen) {
      setFile(undefined);
      return;
    }
    if (chosen.size > MAX_FILE_BYTES) {
      setFile(undefined);
      setError("The file is too large for a single account statement. Export one account at a time");
      return;
    }
    setFile({ name: chosen.name, content: await chosen.text() });
  }

  async function bringIn(): Promise<void> {
    if (!file || !preview) return;
    setSaving(true);
    setError(undefined);
    try {
      const { result } = await api.importStatement({ accountId, fileName: file.name, content: file.content });
      onImported(
        result.importId
          ? `${result.newLineCount} ${result.newLineCount === 1 ? "transaction" : "transactions"} imported from ${file.name}.` +
              (result.closingBalanceCents === null ? "" : ` Statement balance on ${result.to}: ${formatMoney(result.closingBalanceCents)}.`)
          : `No new transactions in ${file.name}. Every transaction was already imported.`,
        accountId,
      );
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not import the statement");
      setSaving(false);
    }
  }

  const canImport = Boolean(preview && preview.newLineCount > 0 && !checking && !saving);

  return (
    <Modal
      open
      size="lg"
      title="Import bank statement"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ti-btn ti-btn-primary-full" disabled={!canImport} onClick={() => void bringIn()}>
            {saving
              ? <Working label="Importing" />
              : preview && preview.newLineCount > 0
                ? `Import ${preview.newLineCount} ${preview.newLineCount === 1 ? "transaction" : "transactions"}`
                : "Import"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-12 gap-4">
        {accounts.length > 1 ? (
          <Field id="import-account" label="Import into account" className="md:col-span-5 col-span-12">
            <SelectField
              id="import-account"
              value={accountId}
              options={accounts.map((account) => ({ value: account.id, label: account.name }))}
              onChange={setAccountId}
            />
          </Field>
        ) : null}
        <Field
          id="import-file"
          label="Statement export (CSV)"
          className={`${accounts.length > 1 ? "md:col-span-7" : ""} col-span-12`}
          hint="OCBC Velocity: Transactions history, select the dates, Export as CSV. Wise: Transactions, Download, CSV."
        >
          {/* The template's file input, from its form elements. */}
          <input
            type="file"
            id="import-file"
            accept=".csv,text/csv"
            className="block w-full border border-gray-200 focus:shadow-sm dark:focus:shadow-white/10 rounded-sm text-sm focus:z-10 focus:outline-0 focus:border-gray-200 dark:focus:border-white/10 dark:border-white/10 file:bg-transparent file:border-0 file:bg-gray-200 file:me-4 file:py-3 file:px-4 dark:file:bg-black/20 dark:file:text-white/50"
            onChange={(event) => void choose(event.target.files?.[0])}
          />
        </Field>
      </div>

      <div className="mt-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}
        {checking ? <Loading /> : null}
        {preview ? <PreviewOf preview={preview} /> : null}
      </div>
    </Modal>
  );
}

function PreviewOf({ preview }: { readonly preview: ImportPreview }): React.ReactElement {
  const rows: [string, React.ReactNode][] = [
    ["Account", preview.accountNumber ? `${preview.accountName}, ${preview.accountNumber}` : preview.accountName],
    ["Statement period", `${preview.from} to ${preview.to}`],
    ["Transactions in the file", preview.lineCount],
    ["New transactions", <span className="font-semibold text-primary">{preview.newLineCount}</span>],
    ["Already imported", preview.alreadyHereCount],
    ...(preview.beforeOpeningCount ? ([["Excluded, before the opening date", preview.beforeOpeningCount]] as [string, React.ReactNode][]) : []),
    ["Funds incoming", <span className="text-success">+{formatMoney(preview.moneyInCents)}</span>],
    ["Funds outgoing", formatMoney(-preview.moneyOutCents)],
    ...(preview.closingBalanceCents === null
      ? []
      : ([["Closing statement balance", <span className="font-semibold">{formatMoney(preview.closingBalanceCents)}</span>]] as [string, React.ReactNode][])),
  ];

  return (
    <>
      {preview.importedBefore ? (
        <Alert tone="info">
          This file was imported on {preview.importedBefore.on} by {preview.importedBefore.by}.{" "}
          {preview.newLineCount === 0
            ? "It contains no new transactions."
            : "Transactions in it that were before the account's start date then can be added now."}
        </Alert>
      ) : preview.newLineCount === 0 ? (
        <Alert tone="info">Every transaction in this file has already been imported. Nothing will be added.</Alert>
      ) : null}
      {preview.warnings.map((warning) => (
        <Alert key={warning} tone="warning">
          {warning}
        </Alert>
      ))}
      {/* The template's flush list group. */}
      <ul className="ti-list-group ti-list-group-flush">
        {rows.map(([label, value], index) => (
          <li
            key={label}
            className={`ti-list-group-item !border-l-0 !border-r-0 ${index === 0 ? "!border-t-0" : ""} ${index === rows.length - 1 ? "!border-b-0" : ""} flex justify-between gap-4`}
          >
            <span className="text-[#8c9097] dark:text-white/50">{label}</span>
            <span className="text-end">{value}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

const KIND_NAMES: Record<MatchKind, string> = {
  category: "Transaction category",
  voucher: "Payment voucher",
  purchase: "Supplier purchase",
  sale: "Sale",
};

/**
 * One line, in the template's right-hand panel: what the bank says, what the system
 * suggests, matching it by hand, setting it aside, or undoing a match with a reason.
 */
function MatchLine({
  line,
  onClose,
  onDone,
}: {
  readonly line: BankLine;
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.ReactElement {
  const [detail, setDetail] = useState<BankLineDetail | undefined>();
  const [kind, setKind] = useState<MatchKind>("category");
  const [targetId, setTargetId] = useState("");
  const [alsoSame, setAlsoSame] = useState(false);
  /** The customer sent more by PayNow and was given the rest back in cash (D, 10 October 2026). */
  const [changeInCash, setChangeInCash] = useState(false);
  /**
   * The customer paid less than the sale because cards were traded in (D, 15 October 2026:
   * $8 Charizard taken in, $148 sold, $140 sent): the buyback's voucher for the difference.
   */
  const [tradeIn, setTradeIn] = useState("");
  /** Paid over the voucher, the rest to come back from the supplier (D, 15 October 2026). */
  const [overpaid, setOverpaid] = useState(false);
  /** The customer paid this sale in more than one transfer (D, 21 October 2026). */
  const [inParts, setInParts] = useState(false);
  const [unpaidVouchers, setUnpaidVouchers] = useState<Voucher[] | undefined>();
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  /** Money in with no sale behind it: key the sale in from here (D, 10 October 2026). */
  const [recording, setRecording] = useState(false);
  /** A payment out that was for several things, split across categories (D, 12 October 2026). */
  const [splitting, setSplitting] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .bankLine(line.id)
      .then((result) => {
        if (!live) return;
        setDetail(result.line);
        // Start on what is most likely: a voucher for money out when there is one to pay.
        // Start on what is most likely: a voucher for money out, a sale for money in.
        setKind(
          result.line.options.vouchers.length > 0
            ? "voucher"
            : result.line.options.sales.length > 0
              ? "sale"
              : "category",
        );
      })
      .catch((problem: unknown) => {
        if (live) setError(problem instanceof ApiError ? problem.message : "Could not load the transaction");
      });
    return () => {
      live = false;
    };
  }, [line.id]);

  async function run(action: () => Promise<string>): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      onDone(await action());
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "The change could not be saved");
      setBusy(false);
    }
  }

  const who = line.counterparty ?? "the same payer or payee";
  const money = formatMoney(line.amountCents);

  const match = (chosenKind: MatchKind, chosenId: string, label: string, same: boolean): Promise<void> =>
    run(async () => {
      const { result } = await api.matchBankLine(line.id, {
        kind: chosenKind,
        targetId: chosenId,
        note: note.trim() || null,
        alsoSameCounterparty: same,
        ...(chosenKind === "sale" && changeInCash ? { changeInCash: true } : {}),
        ...(chosenKind === "sale" && tradeIn ? { tradeInVoucherId: tradeIn } : {}),
        ...(chosenKind === "voucher" && overpaid ? { overpaid: true } : {}),
        ...(chosenKind === "sale" && inParts ? { inParts: true } : {}),
      });
      const others = result.matched - 1;
      return (
        `${describe(line)} is matched to ${label}.` +
        (others > 0 ? ` ${others} more ${others === 1 ? "transaction" : "transactions"} from ${who} also matched.` : "") +
        (result.skippedClosed > 0 ? ` ${result.skippedClosed} in a locked period were left unchanged.` : "")
      );
    });

  const options = detail
    ? kind === "voucher"
      ? detail.options.vouchers
      : kind === "purchase"
        ? detail.options.purchases
        : kind === "sale"
          ? detail.options.sales
          : detail.options.categories
    : [];
  const chosen = options.find((option) => option.id === targetId);
  const kinds: MatchKind[] = detail
    ? (["category", "voucher", "purchase", "sale"] as const).filter(
        (entry) => entry === "category" || detail.options[`${entry}s`].length > 0,
      )
    : ["category"];

  return (
    <Offcanvas wide title={line.amountCents > 0 ? "Funds incoming" : "Funds outgoing"} onClose={onClose}>
      <PanelSection>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className={`font-semibold text-[1.5rem] mb-1 ${line.amountCents > 0 ? "text-success" : ""}`}>
              {line.amountCents > 0 ? `+${money}` : money}
            </p>
            <p className="text-[0.813rem] mb-0">{line.counterparty ?? "No payer or payee name from the bank"}</p>
          </div>
          <div className="text-end text-[0.813rem] text-[#8c9097] dark:text-white/50">
            <p className="mb-1">{line.postDate}</p>
            {line.bankReference ? <p className="mb-0">{line.bankReference}</p> : null}
          </div>
        </div>
        {line.payerNote ? (
          <p className="text-[0.813rem] mt-3 mb-0">
            <span className="text-[#8c9097] dark:text-white/50">Payment reference: </span>
            {line.payerNote}
          </p>
        ) : null}
        <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-2 mb-0">{line.description}</p>
      </PanelSection>

      {detail ? (
        <PanelSection title="Cheque, invoice or receipt">
          <Attachments
            id="line-attach"
            items={detail.attachments}
            onAdd={async (file) => {
              await api.attachToBankLine(line.id, file);
              setDetail((await api.bankLine(line.id)).line);
            }}
            onRemove={async (attachment) => {
              await api.removeBankLineAttachment(line.id, attachment.id);
              setDetail((await api.bankLine(line.id)).line);
            }}
          />
        </PanelSection>
      ) : null}

      {error ? (
        <PanelSection>
          <Alert tone="danger">{error}</Alert>
        </PanelSection>
      ) : null}

      {!detail ? (
        error ? null : (
          <PanelSection last>
            <Loading />
          </PanelSection>
        )
      ) : detail.match || detail.isIgnored ? (
        <>
          <PanelSection title={detail.isIgnored ? "Set aside" : "Linked record"}>
            {detail.isIgnored ? (
              <p className="text-[0.813rem] mb-0">{detail.ignoreReason}</p>
            ) : (
              <>
                <Badge tone={detail.match?.kind === "category" ? "info" : "success"}>{detail.match?.label}</Badge>
                {detail.match?.kind !== "category" && line.accountingCategory ? (
                  <p className="text-[0.813rem] font-semibold mt-2 mb-0">Category: {line.accountingCategory}</p>
                ) : null}
                {/* A category picked by hand over a purchase that has its own: back to the purchase's split. */}
                {(detail.match?.kind === "voucher" || detail.match?.kind === "purchase") && line.categoryConfirmed && (line.categoryParts?.length ?? 0) === 0 ? (
                  <button
                    type="button"
                    className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0 mt-2"
                    onClick={() =>
                      void run(async () => {
                        await api.setBankLineCategory(line.id, null);
                        return `${describe(line)} now uses the categories keyed on its purchase.`;
                      })
                    }
                  >
                    Use the categories on the purchase
                  </button>
                ) : null}
                {/* Any matched line's category can be chosen (D, 16 October 2026). */}
                {detail.match && detail.match.kind !== "category" && detail.options.categories.length > 0 ? (
                  <div className="mt-2 max-w-[20rem]">
                    <SelectField
                      id="line-category"
                      label="Change the category"
                      value=""
                      placeholder="Choose a category"
                      options={detail.options.categories.map((option) => ({ value: option.id, label: option.label }))}
                      onChange={(choice) =>
                        void run(async () => {
                          await api.setBankLineCategory(line.id, choice);
                          return `The category of ${describe(line)} is changed.`;
                        })
                      }
                    />
                  </div>
                ) : null}
                <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-2 mb-0">
                  By {detail.matchedBy} on {detail.matchedAt}
                </p>
                {/* The note as it was written, line by line (D, 11 October 2026). */}
                {detail.matchNote ? <p className="text-[0.813rem] whitespace-pre-line mt-1 mb-0">{detail.matchNote}</p> : null}
                {/* What was bought or sold, money out as well as in (D, 6 October 2026). */}
                {detail.matchedDocument ? (
                  <div className="mt-3 p-3 rounded-md bg-light text-[0.813rem]">
                    <p className="font-semibold mb-2">
                      {detail.matchedDocument.number}, {detail.matchedDocument.day}
                      {detail.matchedDocument.party ? `, ${detail.matchedDocument.party}` : ""}
                    </p>
                    <BankDocumentItems items={detail.matchedDocument.items} expenseItems={detail.matchedDocument.expenseItems} />
                    <DocumentAdjustments
                      subtotalCents={detail.matchedDocument.subtotalCents}
                      discountCents={detail.matchedDocument.discountCents}
                      surchargeCents={detail.matchedDocument.surchargeCents}
                    />
                    <p className="mb-0">
                      Total {formatMoney(detail.matchedDocument.totalCents)}
                      {detail.matchedDocument.paidCents !== null
                        ? `. Paid so far ${formatMoney(detail.matchedDocument.paidCents)}, ${formatMoney(detail.matchedDocument.totalCents - detail.matchedDocument.paidCents)} to go`
                        : ""}
                    </p>
                  </div>
                ) : null}
              </>
            )}
          </PanelSection>
          <PanelSection title={detail.isIgnored ? "Restore transaction" : "Undo match"} last>
            <Field
              id="undo-reason"
              label="Reason"
              hint={
                detail.match?.kind === "voucher"
                  ? "The payment voucher returns to unpaid."
                  : "The transaction returns to unmatched. The reason is retained."
              }
            >
              <input
                id="undo-reason"
                type="text"
                className="form-control"
                value={reason}
                placeholder="Wrong record selected"
                onChange={(event) => setReason(event.target.value)}
              />
            </Field>
            <button
              type="button"
              className="ti-btn ti-btn-danger-full mt-3 !mb-0"
              disabled={busy || !reason.trim()}
              onClick={() =>
                void run(async () => {
                  await api.unmatchBankLine(line.id, reason);
                  return detail.isIgnored ? `${describe(line)} is restored to the unmatched transactions.` : `The match for ${describe(line)} has been undone.`;
                })
              }
            >
              {detail.isIgnored ? "Restore transaction" : "Undo match"}
            </button>
          </PanelSection>
        </>
      ) : (
        <>
          {detail.suggestions.length > 0 ? (
            <PanelSection title="Suggested matches">
              <ul className="ti-list-group ti-list-group-flush">
                {detail.suggestions.map((suggestion, index) => (
                  <li
                    key={`${suggestion.kind}:${suggestion.id}`}
                    className={`ti-list-group-item !border-l-0 !border-r-0 ${index === 0 ? "!border-t-0" : ""} ${index === detail.suggestions.length - 1 ? "!border-b-0" : ""} flex items-center justify-between gap-3 !px-0`}
                  >
                    <TwoLine main={suggestion.label} sub={suggestion.reason} />
                    <button
                      type="button"
                      className="ti-btn ti-btn-success !py-1 !px-2 !text-[0.75rem] !mb-0 shrink-0"
                      disabled={busy}
                      onClick={() => void match(suggestion.kind, suggestion.id, suggestion.label, false)}
                    >
                      <i className="ri-check-line" /> Confirm
                    </button>
                  </li>
                ))}
              </ul>
            </PanelSection>
          ) : null}

          {(detail.transferNotes?.length ?? 0) > 0 ? (
            <PanelSection title="What this transfer was for">
              {detail.transferNotes?.map((note) => (
                <div key={note.at} className="mb-2">
                  <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-0">
                    Written when the OCBC alert came, {new Date(note.at).toLocaleString("en-SG", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", timeZone: "Asia/Singapore" })}
                  </p>
                  <p className="text-[0.875rem] whitespace-pre-line mb-1">{note.description}</p>
                  <button type="button" className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0" onClick={() => setNote(note.description)}>
                    Use as the match note
                  </button>
                </div>
              ))}
            </PanelSection>
          ) : null}
          <PanelSection title={detail.suggestions.length > 0 ? "Or select a record manually" : "Match transaction"}>
            <div className="grid grid-cols-12 gap-4">
              {kinds.length > 1 ? (
                <Field id="match-kind" label="Record type" className="col-span-12">
                  <SelectField<MatchKind>
                    id="match-kind"
                    value={kind}
                    options={kinds.map((entry) => ({ value: entry, label: KIND_NAMES[entry] }))}
                    onChange={(choice) => {
                      setKind(choice);
                      setTargetId("");
                    }}
                  />
                </Field>
              ) : null}
              <Field
                id="match-target"
                label={
                  kind === "category"
                    ? "Transaction category"
                    : kind === "voucher"
                      ? "Payment voucher"
                      : kind === "sale"
                        ? "Sale"
                        : "Supplier purchase"
                }
                className="col-span-12"
                hint={kind === "category" ? "New categories are added on the Transaction categories page." : kind === "voucher" ? "Only unpaid vouchers for this account appear. Changing a date does not reopen a voucher already marked Paid." : undefined}
              >
                <SelectField
                  id="match-target"
                  value={targetId}
                  placeholder="Select"
                  options={options.map((option) => ({ value: option.id, label: option.label }))}
                  onChange={setTargetId}
                />
              </Field>
              {kind === "voucher" && line.amountCents < 0 ? (
                <div className="col-span-12 form-check">
                  <input id="match-overpaid" type="checkbox" className="form-check-input" checked={overpaid} onChange={(event) => setOverpaid(event.target.checked)} />
                  <label htmlFor="match-overpaid" className="form-check-label text-[0.8125rem]">
                    This paid more than the voucher, and the supplier is refunding the rest. Match the refund to the same voucher when it comes in.
                  </label>
                </div>
              ) : null}
              {kind === "voucher" && line.amountCents > 0 ? (
                <p className="col-span-12 text-[0.8125rem] mb-0">Money back from a supplier: it comes off what was paid on the voucher.</p>
              ) : null}
              {kind === "voucher" ? <p className="col-span-12 text-[0.8125rem]">Missing a voucher? <a className="text-primary" href="#/purchases">Review it on Supplier purchases</a>. If it was marked Paid by mistake without a bank match, use Undo payment there before matching.</p> : null}
              {kind === "sale" && chosen && "amountCents" in chosen && typeof chosen.amountCents === "number" && chosen.amountCents !== line.amountCents ? (
                <div className="col-span-12">
                  {chosen.amountCents < line.amountCents ? (
                    <div className="form-check">
                      <input
                        id="match-change"
                        type="checkbox"
                        className="form-check-input"
                        checked={changeInCash}
                        onChange={(event) => setChangeInCash(event.target.checked)}
                      />
                      <label htmlFor="match-change" className="form-check-label">
                        The customer sent {formatMoney(line.amountCents - chosen.amountCents)} more and was given it back in cash. It
                        comes off that day's cash.
                      </label>
                    </div>
                  ) : (
                    <>
                    <div className="form-check mb-2">
                      <input id="match-parts" type="checkbox" className="form-check-input" checked={inParts} onChange={(event) => setInParts(event.target.checked)} />
                      <label htmlFor="match-parts" className="form-check-label">
                        The customer paid this sale in more than one transfer. This line is {formatMoney(line.amountCents)} of it; match the other
                        transfers to the same sale.
                      </label>
                    </div>
                    {inParts ? null : <TradeIn
                      saleCents={chosen.amountCents}
                      paidCents={line.amountCents}
                      vouchers={unpaidVouchers}
                      load={() => {
                        if (!unpaidVouchers) void api.vouchers().then((result) => setUnpaidVouchers(result.vouchers.filter((one) => one.status === "unpaid")));
                      }}
                      value={tradeIn}
                      onChange={setTradeIn}
                    />}
                    </>
                  )}
                </div>
              ) : null}
              {kind === "category" && detail.sameCounterpartyOpen > 0 ? (
                <div className="col-span-12">
                  <div className="form-check">
                    <input
                      id="match-same"
                      type="checkbox"
                      className="form-check-input"
                      checked={alsoSame}
                      onChange={(event) => setAlsoSame(event.target.checked)}
                    />
                    <label htmlFor="match-same" className="form-check-label">
                      Also match the other {detail.sameCounterpartyOpen} unmatched{" "}
                      {detail.sameCounterpartyOpen === 1 ? "transaction" : "transactions"} from {who}
                    </label>
                  </div>
                </div>
              ) : null}
              <Field id="match-note" label="Note" className="col-span-12" hint="Optional. Saved with the match. Enter starts a new line.">
                <textarea
                  id="match-note"
                  rows={3}
                  className="form-control"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </Field>
            </div>
            <button
              type="button"
              className="ti-btn ti-btn-primary-full mt-4 !mb-0"
              disabled={busy || !chosen}
              onClick={() => chosen && void match(kind, chosen.id, chosen.label, kind === "category" && alsoSame)}
            >
              {busy ? <Working label="Matching" /> : "Match transaction"}
            </button>
          </PanelSection>

          {line.amountCents > 0 ? (
            <PanelSection title="No sale for this payment?">
              <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-3">
                For a payment from before the system, or a sale the booth did not record: key in what was sold, on the day the
                money came in, with the photos taken at the time. The payment is matched to it on saving.
              </p>
              <button type="button" className="ti-btn ti-btn-light !mb-0" onClick={() => setRecording(true)}>
                <i className="ri-add-line" /> Record the sale for this payment
              </button>
            </PanelSection>
          ) : null}
          {recording ? (
            <EarlierSale
              start={{ day: line.postDate, amountCents: line.amountCents, accountId: line.accountId, bankLineId: line.id, payer: line.counterparty }}
              onClose={() => setRecording(false)}
              onDone={(message) => {
                setRecording(false);
                onDone(message);
              }}
            />
          ) : null}

          {line.amountCents < 0 ? (
            <PanelSection title="Paid for several things?">
              <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-3">
                One payment that covered more than one category, such as booth rental and stock together. Say what each part was
                for; the bank line stays one payment, and each amount goes under its own category.
              </p>
              <button type="button" className="ti-btn ti-btn-light !mb-0" onClick={() => setSplitting(true)}>
                <i className="ri-git-branch-line" /> Split across categories
              </button>
            </PanelSection>
          ) : null}
          {splitting && detail ? (
            <SplitLine
              line={line}
              categories={detail.options.categories}
              onClose={() => setSplitting(false)}
              onDone={(message) => {
                setSplitting(false);
                onDone(message);
              }}
            />
          ) : null}

          <PanelSection title="Not an actual payment?" last>
            <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-3">
              Use only for a transaction where no funds moved, such as a line the bank printed twice. It is excluded
              from the recorded balance, and account reconciliation will show it.
            </p>
            <div className="flex gap-2">
              <input
                id="ignore-reason"
                type="text"
                aria-label="Why it is set aside"
                className="form-control"
                value={reason}
                placeholder="Printed twice by the bank"
                onChange={(event) => setReason(event.target.value)}
              />
              <button
                type="button"
                className="ti-btn ti-btn-light !mb-0 shrink-0"
                disabled={busy || !reason.trim()}
                onClick={() =>
                  void run(async () => {
                    await api.ignoreBankLine(line.id, reason);
                    return `${describe(line)} is set aside.`;
                  })
                }
              >
                Set aside
              </button>
            </div>
          </PanelSection>
        </>
      )}
    </Offcanvas>
  );
}

/** The first and last day of the month a YYYY-MM-DD day is in, moved by some months. */
function monthOf(day: string, move = 0): { from: string; to: string } {
  const [year, month] = day.split("-").map(Number) as [number, number];
  const first = new Date(Date.UTC(year, month - 1 + move, 1));
  const last = new Date(Date.UTC(year, month + move, 0));
  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
}

/**
 * The statement with what each line was matched to, for the accounts (Daren, 4 October):
 * one row per bank line, matched or not, with the sale, purchase or voucher behind it and
 * what was in it. An Excel file, for the accountant and in the end for the government.
 */
function BankFile({
  accounts,
  onClose,
}: {
  readonly accounts: readonly { id: string; name: string }[];
  readonly onClose: () => void;
}): React.ReactElement {
  const today = singaporeToday();
  const thisMonth = monthOf(today);
  const [from, setFrom] = useState(thisMonth.from);
  const [to, setTo] = useState(today);
  const [accountId, setAccountId] = useState("");
  const quick = [
    { label: "This month", from: thisMonth.from, to: today },
    { label: "Last month", ...monthOf(today, -1) },
    { label: "This year", from: `${today.slice(0, 4)}-01-01`, to: today },
  ];
  const backwards = from > to;

  return (
    <Modal
      open
      title="Export bank reconciliation"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Close
          </button>
          <a
            className={`ti-btn ti-btn-primary-full ${backwards ? "pointer-events-none opacity-50" : ""}`}
            href={api.bankFileUrl(from, to, accountId || undefined)}
            download
            aria-disabled={backwards}
          >
            <i className="ri-download-2-line" /> Download Excel file
          </a>
        </>
      }
    >
      <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-4">
        One row for every bank transaction, matched or unmatched, as shown on the statement. A matched transaction
        includes its linked sale, purchase or payment voucher: the number and date, the customer, the amounts and
        every item sold. Cash sales are not on a bank statement, so with all accounts selected they are included, one
        row each, and the file is in the order the sales were made. One account's file follows the bank statement.
      </p>
      <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Which days">
        {quick.map((choice) => {
          const chosen = from === choice.from && to === choice.to;
          return (
            <button
              key={choice.label}
              type="button"
              aria-pressed={chosen}
              className={`ti-btn !mb-0 ${chosen ? "ti-btn-primary" : "ti-btn-light"}`}
              onClick={() => {
                setFrom(choice.from);
                setTo(choice.to);
              }}
            >
              {choice.label}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-12 gap-4">
        <div className="sm:col-span-6 col-span-12">
          <label htmlFor="bank-file-from" className="form-label">
            Start date
          </label>
          <input
            id="bank-file-from"
            type="date"
            className="form-control"
            value={from}
            max={today}
            onChange={(changed) => setFrom(changed.target.value)}
          />
        </div>
        <div className="sm:col-span-6 col-span-12">
          <label htmlFor="bank-file-to" className="form-label">
            End date
          </label>
          <input
            id="bank-file-to"
            type="date"
            className="form-control"
            value={to}
            max={today}
            onChange={(changed) => setTo(changed.target.value)}
          />
        </div>
        {accounts.length > 1 ? (
          <div className="col-span-12">
            <label htmlFor="bank-file-account" className="form-label">
              Account
            </label>
            <SelectField
              id="bank-file-account"
              value={accountId}
              placeholder="All accounts"
              options={[{ value: "", label: "All accounts" }, ...accounts.map((account) => ({ value: account.id, label: account.name }))]}
              onChange={setAccountId}
            />
          </div>
        ) : null}
      </div>
      {backwards ? <p className="text-[0.75rem] text-warning mt-2 mb-0">The start date is after the end date.</p> : null}
    </Modal>
  );
}

/**
 * One payment out that was for several things (D, 12 October 2026: SGD 9,020 that was 1,520
 * of booth rental over five shows and 7,500 of stock). A line for each thing, with its
 * category, its own words and its amount; the lines must come to the payment. The bank
 * line stays one line and is matched to the record these lines make.
 */
function SplitLine({
  line,
  categories,
  onClose,
  onDone,
}: {
  readonly line: BankLine;
  readonly categories: readonly { id: string; label: string }[];
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.ReactElement {
  const paid = -line.amountCents;
  const [parts, setParts] = useState<{ key: number; categoryId: string; description: string; amount: string }[]>([
    { key: 1, categoryId: "", description: "", amount: "" },
    { key: 2, categoryId: "", description: "", amount: "" },
  ]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const cents = (text: string): number => Math.round(Number(text.replace(/[^\d.]/g, "")) * 100) || 0;
  const allocated = parts.reduce((sum, part) => sum + cents(part.amount), 0);
  const left = paid - allocated;
  const change = (key: number, patch: Partial<{ categoryId: string; description: string; amount: string }>): void =>
    setParts((current) => current.map((part) => (part.key === key ? { ...part, ...patch } : part)));
  // What each category comes to, as the report will show it.
  const byCategory = new Map<string, number>();
  for (const part of parts) if (part.categoryId && cents(part.amount) > 0) byCategory.set(part.categoryId, (byCategory.get(part.categoryId) ?? 0) + cents(part.amount));
  const used = parts.filter((part) => cents(part.amount) > 0);

  async function save(): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      const made = await api.splitBankLine(line.id, {
        note: note.trim() || null,
        parts: used.map((part) => ({ categoryId: part.categoryId, description: part.description.trim(), amountCents: cents(part.amount) })),
      });
      onDone(`${formatMoney(paid)} split across ${byCategory.size} ${byCategory.size === 1 ? "category" : "categories"} and matched to ${made.voucherNumber ?? made.purchaseNumber}. It is listed under Expenses.`);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "The split could not be saved");
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      size="lg"
      title={`Split ${formatMoney(paid)} across categories`}
      onClose={onClose}
      footer={
        <>
          <span className={`me-auto font-semibold ${left === 0 ? "text-success" : "text-danger"}`}>
            Allocated {formatMoney(allocated)} of {formatMoney(paid)}
            {left === 0 ? "" : left > 0 ? `, ${formatMoney(left)} unallocated` : `, ${formatMoney(-left)} too much`}
          </span>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full"
            disabled={busy || left !== 0 || used.length === 0 || used.some((part) => !part.categoryId)}
            onClick={() => void save()}
          >
            {busy ? <Working label="Saving" /> : "Save and match"}
          </button>
        </>
      }
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50">
        {line.counterparty ?? line.description}, {line.postDate}. Add a line for each thing it paid for. Use several lines with the
        same category for a breakdown, such as one line for each show.
      </p>
      {parts.map((part, index) => (
        <div key={part.key} className="grid grid-cols-12 gap-2 mb-2">
          <div className="sm:col-span-4 col-span-12">
            <SelectField
              id={`split-category-${part.key}`}
              value={part.categoryId}
              placeholder="Category"
              options={categories.map((category) => ({ value: category.id, label: category.label }))}
              onChange={(categoryId) => change(part.key, { categoryId })}
            />
          </div>
          <input
            type="text"
            className="form-control sm:col-span-5 col-span-12"
            aria-label={`What line ${index + 1} was for`}
            placeholder="What it was for, e.g. 18-19 July, DOKIDOKI, Singapore Expo"
            value={part.description}
            onChange={(event) => change(part.key, { description: event.target.value })}
          />
          <input
            type="text"
            inputMode="decimal"
            className="form-control sm:col-span-2 col-span-9 text-end"
            aria-label={`Amount of line ${index + 1}`}
            placeholder="0.00"
            value={part.amount}
            onChange={(event) => change(part.key, { amount: event.target.value })}
          />
          <button
            type="button"
            aria-label={`Remove line ${index + 1}`}
            className="ti-btn ti-btn-light !mb-0 sm:col-span-1 col-span-3"
            disabled={parts.length === 1}
            onClick={() => setParts((current) => current.filter((one) => one.key !== part.key))}
          >
            <i className="ri-delete-bin-line" />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="ti-btn ti-btn-light !py-1 !px-3 !text-[0.75rem] !mb-0"
        onClick={() =>
          setParts((current) => [
            ...current,
            // The next line starts with the same category and what is left, which is the usual next step.
            { key: Date.now(), categoryId: current[current.length - 1]?.categoryId ?? "", description: "", amount: left > 0 ? (left / 100).toFixed(2) : "" },
          ])
        }
      >
        <i className="ri-add-line" /> Add line
      </button>
      {byCategory.size > 0 ? (
        <div className="rounded-md bg-light dark:bg-bodybg px-3 py-2 mt-4 text-[0.813rem]">
          {[...byCategory.entries()].map(([id, amount]) => (
            <div key={id} className="flex justify-between">
              <span>{categories.find((category) => category.id === id)?.label}</span>
              <span className="font-semibold">{formatMoney(amount)}</span>
            </div>
          ))}
        </div>
      ) : null}
      <Field id="split-note" label="Note" className="mt-4" hint="Optional. Saved with the match.">
        <textarea id="split-note" rows={2} className="form-control" value={note} onChange={(event) => setNote(event.target.value)} />
      </Field>
    </Modal>
  );
}

/**
 * A sale paid partly in cards (D, 15 October 2026: "we bought the Charizard card for $8, then
 * we sold Dewgong slab for $60 and Lucky Pull for $88", $140 by PayNow). The buyback is keyed
 * with PayNow, which raises its voucher; picking it here settles that voucher by the trade,
 * so the line, the sale and the buyback all agree.
 */
function TradeIn({
  saleCents,
  paidCents,
  vouchers,
  load,
  value,
  onChange,
}: {
  readonly saleCents: number;
  readonly paidCents: number;
  readonly vouchers: readonly Voucher[] | undefined;
  readonly load: () => void;
  readonly value: string;
  readonly onChange: (id: string) => void;
}): React.ReactElement {
  const difference = saleCents - paidCents;
  useEffect(load, []);
  const fitting = (vouchers ?? []).filter((voucher) => voucher.amountCents === difference);
  return (
    <div className="text-[0.8125rem]">
      <p className="mb-2">
        This sale is {formatMoney(saleCents)} and the payment is {formatMoney(paidCents)}. If cards worth {formatMoney(difference)} were
        traded in as part of the deal, pick that buyback's voucher. Otherwise pick the sale of this amount.
      </p>
      {!vouchers ? (
        <Working label="Looking for vouchers" />
      ) : fitting.length === 0 ? (
        <p className="text-danger mb-0">
          No unpaid voucher of {formatMoney(difference)}. Key the buyback in first, with Buy from customer on the selling screen (a past
          day if it was earlier), paid by PayNow, then come back here.
        </p>
      ) : (
        <SelectField
          id="match-trade-in"
          value={value}
          placeholder="The trade-in voucher"
          options={fitting.map((voucher) => ({ value: voucher.id, label: `${voucher.number}, ${voucher.payeeName}, ${formatMoney(voucher.amountCents)}, ${voucher.issuedOn}` }))}
          onChange={onChange}
        />
      )}
    </div>
  );
}

/**
 * The lines that came in twice, each pair side by side, with the copy that is kept (D, 16
 * October 2026: "can you tell us the reason why this is happening?").
 */
function Duplicates({
  groups,
  onClose,
  onSetAside,
  onOne,
}: {
  readonly groups: readonly DuplicateGroup[];
  readonly onClose: () => void;
  readonly onSetAside: () => Promise<void>;
  /** One line set aside by hand: the list is read again. */
  readonly onOne: () => void;
}): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const extra = groups.reduce((sum, group) => sum + group.dropIds.length, 0);
  const stuck = groups.filter((group) => group.lines.length - 1 > group.dropIds.length).length;
  return (
    <Modal
      open
      size="lg"
      title="Lines imported twice"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-warning-full"
            disabled={busy || extra === 0}
            onClick={() => {
              setBusy(true);
              setError(undefined);
              void onSetAside()
                .catch((problem: unknown) => setError(problem instanceof ApiError ? problem.message : "Could not set them aside"))
                .finally(() => setBusy(false));
            }}
          >
            {busy ? <Working label="Setting aside" /> : `Set aside ${extra} extra ${extra === 1 ? "copy" : "copies"}`}
          </button>
        </>
      }
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <p className="text-[0.813rem]">
        Why: an OCBC export taken during the day lists a payment that came in after the bank's cut-off with that day as its post
        date. The next export lists the same payment with the next day. The system now also matches lines by value date and amount
        with the same balance or the same words, so a statement imported from now on cannot do this. These came in before that.
      </p>
      <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50">
        The copy kept is the one already matched, or else the one with the later post date, as the final statement shows it. The
        other is set aside with the reason, and leaves the balance.
        {stuck > 0 ? ` ${stuck} ${stuck === 1 ? "pair has" : "pairs have"} both copies matched: undo one of the matches first.` : ""}
      </p>
      <Table
        head={
          <>
            <Th>Post date</Th>
            <Th>Value date</Th>
            <Th>Payer or payee</Th>
            <Th end>Amount</Th>
            <Th end>Balance after</Th>
            <Th>What happens</Th>
          </>
        }
      >
        {groups.flatMap((group) =>
          group.lines.map((line, index) => (
            <tr key={line.id} className={`border-b border-defaultborder ${index === group.lines.length - 1 ? "border-b-2" : ""}`}>
              <td>{line.postDate}</td>
              <td>{line.valueDate}</td>
              <td>{line.counterparty ?? ""}</td>
              <td className="!text-end">{formatMoney(line.amountCents)}</td>
              <td className="!text-end">{line.balanceAfterCents === null ? "" : formatMoney(line.balanceAfterCents)}</td>
              <td>
                <div className="flex flex-wrap items-center gap-2">
                  {line.id === group.keepId ? (
                    <Badge tone="success">Kept{line.matched ? ", matched" : ""}</Badge>
                  ) : group.dropIds.includes(line.id) ? (
                    <Badge tone="warning">Set aside</Badge>
                  ) : (
                    <Badge tone="danger">Matched: undo first</Badge>
                  )}
                  {/* This one only (D, 16 October 2026: "right now it only allows me to set aside all at once"). */}
                  {!line.matched ? (
                    <button
                      type="button"
                      className="ti-btn ti-btn-light !py-0.5 !px-2 !text-[0.6875rem] !mb-0"
                      disabled={busy}
                      onClick={() => {
                        setBusy(true);
                        setError(undefined);
                        void api
                          .ignoreBankLine(line.id, `Imported twice: the same line as another posted ${group.lines.find((one) => one.id !== line.id)?.postDate ?? ""}`)
                          .then(onOne)
                          .catch((problem: unknown) => setError(problem instanceof ApiError ? problem.message : "Could not set it aside"))
                          .finally(() => setBusy(false));
                      }}
                    >
                      Set aside this one
                    </button>
                  ) : null}
                </div>
              </td>
            </tr>
          )),
        )}
      </Table>
    </Modal>
  );
}
